import {
  ReferenceLayoutSkeleton,
  SuspensePageBoundary,
} from "@-/dev.improve.haniverse.components.ui.suspense-page";
import { usePrefetchedSuspenseQuery } from "@-/com.npmjs.tanstack__react-query";
import { View } from "@-/com.npmjs.react-native";
import type { ReferenceGraphemeMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { useInfoGraphemeMorphemeSliceQueryOptions } from "@-/dev.improve.haniverse.lib.morpheme.read";
import { MorphemeNotFound } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.not-found";
import {
  MorphemeInfoPanelGroupedSidebar,
  MorphemePanelContent,
  useMorphemeInfoPanelGraphemeGroupedSections,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content";
import {
  GraphemeMnemonicsMeaningReferenceSection,
  useGraphemeMnemonicsPronunciationReferenceSectionList,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics";
import { MnemonicGateReferenceSurfaceProvider } from "@-/dev.improve.haniverse.lib.morpheme.mnemonic-gate-surface";
import { GraphemeReferenceSection } from "#grapheme-reference-section";

/**
 * Meaning + one block per reading, rendered above the info sections.
 *
 * Split out behind its own boundary: the per-reading screens it renders load their own data, so a
 * slow one never blanks the info sections below. The reading list itself comes from the panels
 * context the page layout already narrowed.
 */
function GraphemeReferenceMnemonicsHeaderSuspense({ grapheme }: { grapheme: string }) {
  const pronunciationSectionList = useGraphemeMnemonicsPronunciationReferenceSectionList(grapheme);

  return (
    <>
      <GraphemeReferenceSection title="Meaning">
        <GraphemeMnemonicsMeaningReferenceSection />
      </GraphemeReferenceSection>
      {pronunciationSectionList.map((section) => (
        <GraphemeReferenceSection key={section.toneNumbersPinyin} title={section.title}>
          {section.render()}
        </GraphemeReferenceSection>
      ))}
    </>
  );
}

/**
 * The rail + scroll body. Kept separate from the provider below because the section hooks it calls
 * read `MorphemeInfoPanelContext` (via their own sub-hooks), so they can only run INSIDE
 * `MorphemePanelContent` — calling them in the component that renders the provider would throw
 * "MorphemeInfoPanelContext can only be accessed within a <MorphemeInfoPanelContextProvider>".
 */
function GraphemeReferenceBody({
  grapheme,
  graphemeCard,
}: {
  grapheme: string;
  graphemeCard: ReferenceGraphemeMorphemeSlice;
}) {
  const groups = useMorphemeInfoPanelGraphemeGroupedSections({ graphemeCard });

  return (
    <MorphemeInfoPanelGroupedSidebar
      groups={groups}
      header={
        <SuspensePageBoundary
          fallback={<ReferenceLayoutSkeleton />}
          location={import.meta.location}
        >
          <GraphemeReferenceMnemonicsHeaderSuspense grapheme={grapheme} />
        </SuspensePageBoundary>
      }
    />
  );
}

function GraphemeReferenceViewSuspense({ grapheme }: { grapheme: string }) {
  // The info slice the panel sections read from. The info PANEL screens each fetch this themselves
  // behind their own boundary; Reference fetches it once and shares it, since every group needs it.
  // Prefetched by the page navigator, so this is normally a cache hit.
  const infoGraphemeMorphemeSliceQueryOptions = useInfoGraphemeMorphemeSliceQueryOptions(grapheme);
  const { data: infoBackCardSlice } = usePrefetchedSuspenseQuery(
    infoGraphemeMorphemeSliceQueryOptions,
  );

  // Reference is only mounted for a single grapheme (the panels layout branches words to
  // `MorphemePanelsWordView` first), so a non-grapheme slice here means the character has no card.
  if (infoBackCardSlice === null || infoBackCardSlice.morphemeType !== "grapheme") {
    return <MorphemeNotFound morpheme={grapheme} />;
  }

  return (
    <MorphemePanelContent
      infoBackCardSlice={infoBackCardSlice}
      infoBackCardSliceQueryKey={infoGraphemeMorphemeSliceQueryOptions.queryKey}
    >
      <GraphemeReferenceBody grapheme={grapheme} graphemeCard={infoBackCardSlice} />
    </MorphemePanelContent>
  );
}

/**
 * The single-scroll grapheme detail page: a section rail on the left, everything expanded in one
 * continuous scroll on the right, instead of the Info/Mnemonics pager and its nested tabs.
 *
 * The info sections arrive as DESCRIPTORS from `useMorphemeInfoPanelGraphemeGroupedSections` rather
 * than as rendered tab components — mounting the five tabs would stack five scroll containers, each
 * `flex-1` and therefore zero-height inside this page's scroll, silently blanking them all.
 *
 * The mnemonics content rides along as the sidebar's `header`: it comes from the mnemonics package
 * (and `GraphemeMnemonicsMeaningReferenceSection` reads its character from `MorphemePanelsContext`),
 * so it has no info-section descriptor to slot into a group.
 */
export function GraphemeReferenceView({ grapheme }: { grapheme: string }) {
  return (
    // Reference is the ONLY surface that gates a free mnemonic behind a reveal button. It is a
    // web-only, direct-link (SEO) page, so that speed bump and its account pitch land on search
    // visitors — never on the Info/Mnemonics panels people use in the app, which are also the only
    // views reachable at all on native.
    <MnemonicGateReferenceSurfaceProvider>
      <View className="flex-1" testID="grapheme-reference-scroll">
        <SuspensePageBoundary
          fallback={<ReferenceLayoutSkeleton />}
          location={import.meta.location}
        >
          <GraphemeReferenceViewSuspense grapheme={grapheme} />
        </SuspensePageBoundary>
      </View>
    </MnemonicGateReferenceSurfaceProvider>
  );
}
